import React, { useEffect, useState } from "react";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../firebaseConfig";
import { getAuth } from "firebase/auth";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import "./global.css"; 

const Home = () => {
  const [jobs, setJobs] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userFullName, setUserFullName] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]); // Update to handle multiple files
  const [uploading, setUploading] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // Search term
  const [sortOption, setSortOption] = useState({ column: '', direction: '' }); // Sort option
  const [filteredJobs, setFilteredJobs] = useState([]); // Filtered jobs for search and sort

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (currentUser) {
        setUserId(currentUser.uid);

        const usersRef = collection(db, "users");
        const querySnapshot = await getDocs(usersRef);
        const userDoc = querySnapshot.docs.find((doc) => doc.id === currentUser.uid);

        if (userDoc) {
          setUserRole(userDoc.data().role);
          setUserFullName(`${userDoc.data().firstName} ${userDoc.data().lastName}`);
        }
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchJobs = async () => {
      const jobsRef = collection(db, "jobs");
      const querySnapshot = await getDocs(jobsRef);

      const allJobs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Log all jobs for debugging
      console.log("All Jobs:", allJobs);

      const visibleJobs = allJobs.filter(
        job =>
          typeof job.techAssign === "string" &&
          job.techAssign.trim().toLowerCase() === "yosaf konde" && // Match techAssign for Yosaf Konde
          job.status === "Active" // Only show jobs with status "Active"
      );

      console.log("Filtered Jobs:", visibleJobs);
      setJobs(visibleJobs);
    };

    fetchJobs();
  }, []);

  useEffect(() => {
    filterAndSortJobs();
  }, [jobs, searchTerm, sortOption]);

  const closeModal = () => setSelectedJob(null);
  const closeNotePopup = () => setShowNotePopup(false);
  const closeVerificationPopup = () => setShowVerificationPopup(false);

  const markJobCompleted = async () => {
    if (selectedJob) {
      const jobDoc = doc(db, "jobs", selectedJob.id);
      await updateDoc(jobDoc, { status: "Completed" });
      setJobs((prevJobs) => prevJobs.filter((job) => job.id !== selectedJob.id));
      closeModal();
      closeVerificationPopup();
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setSelectedJob((prev) => ({
      ...prev,
      newFiles: files.map(file => URL.createObjectURL(file))
    }));
  };

  const uploadFiles = async (files) => {
    const storage = getStorage();
    const uploadPromises = files.map(async (file) => {
      const storageRef = ref(storage, `notes/${selectedJob.id}/${file.name}`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    });
    return await Promise.all(uploadPromises);
  };

  const saveNote = async () => {
    if (selectedJob && (noteText.trim() !== "" || selectedFiles.length > 0)) {
      setUploading(true);
      let fileURLs = [];

      if (selectedFiles.length > 0) {
        fileURLs = await uploadFiles(selectedFiles);
      }

      const jobDoc = doc(db, "jobs", selectedJob.id);
      const newNote = {
        text: noteText,
        timestamp: new Date().toLocaleString(),
        author: userFullName,
        fileURLs,
        fileNames: selectedFiles.map(file => file.name),
      };

      const updatedNotes = Array.isArray(selectedJob.notes) ? [...selectedJob.notes, newNote] : [newNote];
      await updateDoc(jobDoc, { notes: updatedNotes });

      setSelectedJob((prev) => ({ ...prev, notes: updatedNotes }));
      setNoteText("");
      setSelectedFiles([]);
      setUploading(false);
      closeNotePopup();
    }
  };

  const getBackgroundColor = (startDate) => {
    const date = moment(startDate);
    const now = moment();
    const diffDays = now.diff(date, 'days');

    if (diffDays > 30) {
      return 'red';
    } else if (diffDays > 1) {
      return 'orange';
    } else {
      return 'white';
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const SortButton = ({ columnKey, sortOption, onSort }) => (
    <button onClick={() => onSort(columnKey)}>
      {sortOption.column === columnKey ? (sortOption.direction === 'asc' ? '↑' : '↓') : '↕'}
    </button>
  );

  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSortChange = (column) => {
    let direction = 'asc';
    if (sortOption.column === column && sortOption.direction === 'asc') {
      direction = 'desc';
    }
    setSortOption({ column, direction });
  };

  const filterAndSortJobs = () => {
    let filtered = jobs.filter(job =>
      job.siteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.management.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredJobs(filtered);
  };

  return (
    <div>
      <h1 className="center-text">Welcome to the Portal</h1>
      <p className="center-text">Role: {userRole}</p>

      <div className="dashboard-section">
        <h2>Jobs</h2>
        <div className="search-sort-container">
          <input
            type="text"
            placeholder="Search by Site Name or Management"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        {filteredJobs.length > 0 ? (
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>
                    WO# <SortButton columnKey="wo" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>
                    Site Name <SortButton columnKey="siteName" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>
                    City <SortButton columnKey="city" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>
                    Start Date <SortButton columnKey="startDate" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>
                    Service Type <SortButton columnKey="serviceType" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>Scope</th>
                  <th>
                    Tech <SortButton columnKey="techAssign" sortOption={sortOption} onSort={handleSortChange} />
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortData(filteredJobs, sortOption.column, sortOption.direction).map((job) => (
                  <tr key={job.id} style={{ backgroundColor: getBackgroundColor(job.startDate) }}>
                    <td>{job.wo}</td>
                    <td>{job.siteName}</td>
                    <td>{job.city}</td>
                    <td>{job.startDate}</td>
                    <td>{job.serviceType}</td>
                    <td>{job.scope}</td>
                    <td>{job.techAssign}</td>
                    <td>
                      <button className="azul-button" onClick={() => setSelectedJob(job)}>Details</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No jobs available.</p>
        )}
      </div>  

      {selectedJob && (
        <>
          <div className="modal-overlay" onClick={closeModal}></div>
          <div className="modal">
            <h2>Job Details</h2>
            <p><strong>Site Name:</strong> {selectedJob.siteName}</p>
            <p><strong>Work Order:</strong> {selectedJob.wo}</p>
            <p><strong>Address:</strong> {selectedJob.address}</p>
            <p><strong>City:</strong> {selectedJob.city}</p>
            <p><strong>State:</strong> {selectedJob.state}</p>
            <p><strong>Start Date:</strong> {selectedJob.startDate}</p>
            <p><strong>End Date:</strong> {selectedJob.endDate}</p>
            <p><strong>Service Type:</strong> {selectedJob.serviceType}</p>
            <p><strong>Scope:</strong> {selectedJob.scope}</p>
            <p><strong>Job Description:</strong> {selectedJob.jobDescription}</p>
            <p><strong>Tech Assign:</strong> {selectedJob.techAssign}</p>
            <p><strong>Status:</strong> {selectedJob.status}</p>

            <h3>Notes</h3>
            <div className="notes-container">
              {Array.isArray(selectedJob.notes) && selectedJob.notes.length > 0 ? (
                <ul>
                  {selectedJob.notes.map((note, index) => (
                    <li key={index}>
                      <strong>{note.author} ({note.timestamp}):</strong> {note.text}
                      {note.files && note.files.map((file, fileIndex) => (
  <p key={fileIndex}>
    <a href={file.fileURL} target="_blank" rel="noopener noreferrer">
      {file.fileURL.match(/\.(jpeg|jpg|gif|png)$/i) ? (
        <img src={file.fileURL} alt={`Attachment ${fileIndex + 1}`} className="thumbnail" />
      ) : (
        `View Attachment ${fileIndex + 1}`
      )}
    </a>
  </p>
))}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No notes yet.</p>
              )}
            </div>

            {selectedJob.fileURLs && selectedJob.fileURLs.length > 0 && (
              <div>
                <h3>Uploaded Files</h3>
                <div className="uploaded-files">
                  {selectedJob.fileURLs.map((fileURL, index) => (
                    <div key={index}>
                      <a href={fileURL} target="_blank" rel="noopener noreferrer">
                        {fileURL.match(/\.(jpeg|jpg|gif|png)$/) ? (
                          <img src={fileURL} alt={`File ${index + 1}`} className="thumbnail" />
                        ) : (
                          `View File ${index + 1}`
                        )}
                      </a>
                      <p>{selectedJob.fileNames && selectedJob.fileNames[index]}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedJob.newFiles && selectedJob.newFiles.length > 0 && (
              <div>
                <h3>New Attachments</h3>
                <div className="new-attachments">
                  {selectedJob.newFiles.map((fileURL, index) => (
                    <img key={index} src={fileURL} alt={`New Attachment ${index + 1}`} className="thumbnail" />
                  ))}
                </div>
              </div>
            )}

            <div className="modal-buttons">
              <button className="azul-button" onClick={() => setShowNotePopup(true)}>Add Note</button>
              <button className="green-button" onClick={() => setShowVerificationPopup(true)}>Mark Job as Completed</button>
              <button className="red-button" onClick={closeModal}>Close</button>
            </div>
          </div>
        </>
      )}

      {showNotePopup && (
        <>
          <div className="modal-overlay" onClick={closeNotePopup}></div>
          <div className="modal">
            <h2>Add Note</h2>
            <textarea className="form-container" rows="4" placeholder="Enter your note here..." value={noteText} onChange={(e) => setNoteText(e.target.value)}></textarea>
            <input type="file" accept="image/*, .pdf" onChange={handleFileChange} multiple />
            <div className="modal-buttons">
              <button className="green-button" onClick={saveNote} disabled={uploading}>{uploading ? "Uploading..." : "Save"}</button>
              <button className="red-button" onClick={closeNotePopup}>Cancel</button>
            </div>
          </div>
        </>
      )}

      {showVerificationPopup && (
        <>
          <div className="modal-overlay" onClick={closeVerificationPopup}></div>
          <div className="modal">
            <h3>Are you sure you want to complete this job?</h3>
            <div className="button-group">
              <button className="shortgreen-button" onClick={markJobCompleted}>Yes</button>
              <button className="shortred-button" onClick={closeVerificationPopup}>No</button>
            </div>
          </div>
        </>
      )}

      {/** Admin Portal Button **/}
      {userRole === "Admin" && (
        <div className="admin-portal-container">
          <button className="smallazul-button" onClick={() => navigate("/AdminPortal")}>
            Go to Admin Portal
          </button>
        </div>
      )}
    </div>
  );
};

export default Home;